import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useTenantAuth } from "@/context/TenantAuthContext";
import { Loader2, ArrowRight, ShieldCheck } from "lucide-react";

export default function TenantActivate() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { refetch } = useTenantAuth();
  const [info, setInfo] = useState(null); // null=loading, false=invalid, obj=ok
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) { setInfo(false); return; }
    api.get(`/tenant/auth/activate/${token}`).then(({ data }) => setInfo(data)).catch(() => setInfo(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pin.length !== 6) return setError("暗証番号は6桁で入力してください");
    if (pin !== pin2) return setError("暗証番号が一致しません");
    setLoading(true);
    try {
      await api.post("/tenant/auth/activate", { token, pin });
      await refetch();
      navigate("/tenant", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "登録に失敗しました");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111] text-white flex items-center justify-center px-5" data-testid="tenant-activate-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <img src="/logo.png" alt="Neo Real Estate Management SaaS" className="h-14 w-auto object-contain bg-white rounded-lg p-1.5 shrink-0" />
          <div>
            <div className="font-display font-semibold text-lg tracking-wide">NEO REAL ESTATE</div>
            <div className="text-xs text-gray-500 tracking-wider">借主ポータル 登録</div>
          </div>
        </div>

        {info === null && <div className="text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />確認中...</div>}
        {info === false && (
          <div className="bg-[#1b1b1b] border border-white/10 rounded-lg p-6" data-testid="activate-invalid">
            <h1 className="text-xl font-light mb-2">リンクが無効です</h1>
            <p className="text-sm text-gray-400 mb-4">この招待リンクは無効か、有効期限（7日間）が切れています。お手数ですが管理会社に再発行をご依頼ください。</p>
            <Link to="/tenant/login" className="text-brand text-sm hover:underline">ログイン画面へ</Link>
          </div>
        )}
        {info && (
          <>
            <h1 className="font-display text-3xl font-light mb-2">ようこそ、{info.name} 様</h1>
            <p className="text-sm text-gray-400 mb-6">ご利用にあたり、ログイン用の暗証番号（6桁）を設定してください。</p>
            <div className="bg-[#1b1b1b] border border-white/10 rounded-md px-4 py-3 mb-5">
              <div className="text-xs text-gray-500">ログインコード</div>
              <div className="text-lg font-mono tracking-widest" data-testid="activate-login-code">{info.login_code}</div>
            </div>
            <form onSubmit={submit} className="space-y-5">
              <div>
                <label className="block text-sm text-gray-300 mb-1.5">暗証番号（6桁）</label>
                <input value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" type="password" placeholder="••••••"
                  className="w-full bg-[#1b1b1b] border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:border-brand tracking-[0.5em]" data-testid="activate-pin" />
              </div>
              <div>
                <label className="block text-sm text-gray-300 mb-1.5">暗証番号（確認）</label>
                <input value={pin2} onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" type="password" placeholder="••••••"
                  className="w-full bg-[#1b1b1b] border border-white/10 rounded-md px-4 py-3 text-white focus:outline-none focus:border-brand tracking-[0.5em]" data-testid="activate-pin2" />
              </div>
              {error && <div className="text-sm text-red-400" data-testid="activate-error">{error}</div>}
              <button type="submit" disabled={loading} className="w-full inline-flex items-center justify-center gap-2 bg-brand hover:bg-brand-hover disabled:opacity-50 text-white font-medium px-6 py-3.5 rounded-md" data-testid="activate-submit">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}登録してはじめる{!loading && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
